import React, { useMemo } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import RouteKey from './key';
import PrivateRoute from '../modules/profile/components/PrivateRoute';
import { Role } from '../models';

import Listing from '../pages/examRegistration/Listing';
import Instruction from '../pages/withdrawal/Instructions';
import WithdrawalReasonPage from '../pages/withdrawal/WithdrawalReasonPage';
import ReviewWithdrawalPage from '../pages/withdrawal/ReviewWithdrawalPage';
import ViewApplicationPage from '../pages/withdrawal/ViewApplicationPage';
import SubmitApplicationPage from '../pages/withdrawal/SubmitApplicationPage';
import UploadAdditionalDocumentPage from '../pages/withdrawal/UploadAdditionalDocumentPage';
import ListingPage from '../pages/withdrawal/coordinator/ListingPage';
import InstructionPage from '../pages/withdrawal/coordinator/InstructionPage';
import CoordinatorWithdrawalReasonPage from '../pages/withdrawal/coordinator/WithdrawalReasonPage';
import SelectCandidatesPage from '../pages/withdrawal/coordinator/SelectCandidatesPage';
import ReviewPage from '../pages/withdrawal/coordinator/ReviewPage';
import SubmitPage from '../pages/withdrawal/coordinator/SubmitPage';
import ViewPage from '../pages/withdrawal/coordinator/ViewPage';
import WithdrawalInstructions from '../pages/withdrawal/NonNationalExam/WithdrawalInstructions';
import WithdrawalReason from '../pages/withdrawal/NonNationalExam/WithdrawalReason';
import ReviewWithdrawal from '../pages/withdrawal/NonNationalExam/ReviewWithdrawal';
import WithdrawalReceipt from '../pages/withdrawal/NonNationalExam/WithdrawalReceipt';
import WithdrawalSummary from '../pages/withdrawal/NonNationalExam/WithdrawalSummary';
import UploadAdditionalDocument from '../pages/withdrawal/NonNationalExam/UploadAdditionalDocument';

const { error: errorRoute } = RouteKey.child;
const {
  examRegistrations,
  instruction,
  reason,
  review,
  selectCandidates,
  submitted,
  summary,
  additionalDocuments,
  withdrawal,
  nonNationalInstruction,
  nonNationalReason,
  nonNationalReview,
  nonNationalReceipt,
  nonNationalSummary,
  nonNationalAdditionalDocuments,
} = RouteKey.withdrawal.child;

export const WithdrawalNavigationContext = React.createContext<{
  toStartWithdrawalInstruction: string;
  toWithdrawalReason: string;
  toReviewWithdrawal: string;
}>({
  toStartWithdrawalInstruction: '',
  toWithdrawalReason: '',
  toReviewWithdrawal: '',
});

const WithdrawalRoute = () => {
  const { path } = useRouteMatch();

  const routeSteps = [
    {
      label: 'Reason for Withdrawal',
    },
    {
      label: 'Review Details',
    },
  ];

  const coordinatorRouteSteps = [
    {
      label: 'Reason for Withdrawal',
    },
    {
      label: 'Select Candidates & Upload Documents',
    },
    {
      label: 'Review Application',
    },
  ];

  const contextValue = useMemo(
    () => ({
      toStartWithdrawalInstruction: `${path}/${examRegistrations.route}/${instruction.route}`,
      toWithdrawalReason: `${path}/${examRegistrations.route}/:uuid/${reason.route}`,
      toReviewWithdrawal: `${path}/${examRegistrations.route}/:uuid/${review.route}`,
    }),
    [path]
  );

  return (
    <WithdrawalNavigationContext.Provider value={contextValue}>
      <Switch>
        <PrivateRoute exact path={`${path}/${examRegistrations.route}`} roles={[Role.candidate]}>
          <Listing />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/${instruction.route}`}
          roles={[Role.candidate]}
        >
          <Instruction />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${reason.route}`}
          roles={[Role.candidate]}
        >
          <WithdrawalReasonPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${review.route}`}
          roles={[Role.candidate]}
        >
          <ReviewWithdrawalPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${summary.route}`}
          roles={[Role.candidate]}
        >
          <ViewApplicationPage />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${submitted.route}`}
          roles={[Role.candidate]}
        >
          <SubmitApplicationPage />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${additionalDocuments.route}`}
          roles={[Role.candidate]}
        >
          <UploadAdditionalDocumentPage routeSteps={routeSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${examRegistrations.route}/${nonNationalInstruction.route}`}
          roles={[Role.candidate]}
        >
          <WithdrawalInstructions />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${nonNationalReason.route}`}
          roles={[Role.candidate]}
        >
          <WithdrawalReason />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${nonNationalReview.route}`}
          roles={[Role.candidate]}
        >
          <ReviewWithdrawal />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${nonNationalReceipt.route}`}
          roles={[Role.candidate]}
        >
          <WithdrawalReceipt />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${nonNationalSummary.route}`}
          roles={[Role.candidate]}
        >
          <WithdrawalSummary />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${examRegistrations.route}/:uuid/${nonNationalAdditionalDocuments.route}`}
          roles={[Role.candidate]}
        >
          <UploadAdditionalDocument />
        </PrivateRoute>

        <PrivateRoute exact path={`${path}/${withdrawal.route}`} roles={[Role.coordinator]}>
          <ListingPage />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${withdrawal.route}/${instruction.route}/:examTypeCode`}
          roles={[Role.coordinator]}
        >
          <InstructionPage />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${withdrawal.route}/:uuid/${reason.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorWithdrawalReasonPage routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>

        <PrivateRoute
          path={`${path}/${withdrawal.route}/:uuid/${selectCandidates.route}`}
          roles={[Role.coordinator]}
        >
          <SelectCandidatesPage routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${withdrawal.route}/:uuid/${review.route}`}
          roles={[Role.coordinator]}
        >
          <ReviewPage routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${withdrawal.route}/:uuid/${submitted.route}`}
          roles={[Role.coordinator]}
        >
          <SubmitPage />
        </PrivateRoute>

        <PrivateRoute
          exact
          path={`${path}/${withdrawal.route}/:uuid/${summary.route}`}
          roles={[Role.coordinator]}
        >
          <ViewPage />
        </PrivateRoute>

        <Route path="*" exact>
          <Redirect to={`/${errorRoute}`} />
        </Route>
      </Switch>
    </WithdrawalNavigationContext.Provider>
  );
};

export default WithdrawalRoute;
